/**
 * Patient Authentication Context
 *
 * Manages patient authentication state and JWT token handling
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { authApi, type PatientProfile } from '@/services/authApi';
import {
  setAuthToken,
  clearAuthToken,
  getAuthToken,
  setRefreshToken,
  clearRefreshToken,
  ApiError
} from '@/services/api';

interface PatientAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  profile: PatientProfile | null;
  token: string | null;
}

interface PatientAuthContextType extends PatientAuthState {
  login: (participantId: string, pin: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  loginWithToken: (accessToken: string, refreshToken: string) => void;
  verifyActivationCode: (activationCode: string) => Promise<{ success: boolean; resetToken?: string; participantId?: string; error?: string }>;
  setupPin: (resetToken: string, newPin: string) => Promise<{ success: boolean; error?: string }>;
  refreshProfile: () => Promise<void>;
}

const PatientAuthContext = createContext<PatientAuthContextType | null>(null);

export const PatientAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<PatientAuthState>({
    isAuthenticated: false,
    isLoading: true,
    profile: null,
    token: null,
  });

  // Initialize from localStorage on mount
  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      setState(prev => ({ ...prev, token, isAuthenticated: true, isLoading: false }));
      // Note: fetchProfile을 자동으로 호출하지 않음
      // 필요한 곳에서 refreshProfile()을 명시적으로 호출해야 함
    } else {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const fetchProfile = async () => {
    try {
      const profile = await authApi.fetchProfile();
      setState(prev => ({
        ...prev,
        profile,
        isLoading: false,
      }));
    } catch (error) {
      // Token refresh will be handled automatically by api.ts interceptor
      // If we reach here, it means refresh also failed
      clearAuthToken();
      clearRefreshToken();
      setState({
        isAuthenticated: false,
        isLoading: false,
        profile: null,
        token: null,
      });
    }
  };

  const login = useCallback(async (participantId: string, pin: string) => {
    try {
      const response = await authApi.pinLogin(participantId, pin);

      const { accessToken, refreshToken, participant } = response;

      // Store tokens
      setAuthToken(accessToken);
      setRefreshToken(refreshToken);

      setState({
        isAuthenticated: true,
        isLoading: false,
        profile: {
          participantId: participant.participantId,
          projectId: participant.projectId,
          status: participant.status,
        },
        token: accessToken,
      });

      return { success: true };
    } catch (error) {
      const message = error instanceof ApiError
        ? error.message
        : 'Login failed';
      return { success: false, error: message };
    }
  }, []);

  // 토큰 기반 인증 완료 후 컨텍스트 상태 동기화
  // TokenAuthPage 처럼 setAuthToken()을 직접 호출하는 경로에서 사용
  const loginWithToken = useCallback((accessToken: string, refreshToken: string) => {
    setAuthToken(accessToken);
    if (refreshToken) setRefreshToken(refreshToken);
    setState({
      isAuthenticated: true,
      isLoading: false,
      profile: null,
      token: accessToken,
    });
  }, []);

  const logout = useCallback(() => {
    authApi.logout();
    clearRefreshToken();
    setState({
      isAuthenticated: false,
      isLoading: false,
      profile: null,
      token: null,
    });
  }, []);

  const verifyActivationCode = useCallback(async (activationCode: string) => {
    try {
      const { resetToken, participantId } = await authApi.verifyActivationCode(activationCode);
      return { success: true, resetToken, participantId };
    } catch (error) {
      const message = error instanceof ApiError
        ? error.message
        : 'Invalid activation code';
      return { success: false, error: message };
    }
  }, []);

  const setupPin = useCallback(async (resetToken: string, newPin: string) => {
    try {
      await authApi.setupPin(resetToken, newPin);
      return { success: true };
    } catch (error) {
      const message = error instanceof ApiError
        ? error.message
        : 'Failed to set PIN';
      return { success: false, error: message };
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (state.token) {
      await fetchProfile();
    }
  }, [state.token]);

  const contextValue = useMemo(() => ({
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    profile: state.profile,
    token: state.token,
    login,
    logout,
    loginWithToken,
    verifyActivationCode,
    setupPin,
    refreshProfile,
  }), [state.isAuthenticated, state.isLoading, state.profile, state.token, login, logout, loginWithToken, verifyActivationCode, setupPin, refreshProfile]);

  return (
    <PatientAuthContext.Provider value={contextValue}>
      {children}
    </PatientAuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const usePatientAuth = () => {
  const context = useContext(PatientAuthContext);
  if (!context) {
    throw new Error('usePatientAuth must be used within a PatientAuthProvider');
  }
  return context;
};
